import { API, PolicyWorksheetViewDTO } from "@swivl-mono/api-helper";
import { POLICY_NEW_FIELD_MAPKEYS } from "@swivl-mono/types";
import { Spinner } from "@swivl-mono/ui";
import { useCallback, useEffect, useState } from "react";

interface AdditionalCustomizationsTabProps {
  orgId: number;
}

interface NewFieldGroup {
  policyKey: string;
  title: string;
  fields: { mapKey: string; label: string }[];
}

/**
 * Step 3 — "New in v3": policy fields with no legacy source (the operator fills them out), grouped
 * by worksheet. Read-only; applying the Policies step opens the Swivl-Support ticket that lists these
 * as tasks.
 */
export function AdditionalCustomizationsTab({ orgId }: AdditionalCustomizationsTabProps): React.ReactElement {
  const [worksheets, setWorksheets] = useState<PolicyWorksheetViewDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.PolicyWorksheets.getWorksheets(orgId);
      setWorksheets(res.worksheets);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    reload().catch(() => {
      /* best-effort */
    });
  }, [reload]);

  if (loading) {
    return (
      <div className="body-sm mx-auto flex max-w-5xl items-center gap-2 p-6 text-grey-400">
        <Spinner /> Loading…
      </div>
    );
  }

  const labelByMapKey = new Map<string, string>();
  const titleByPolicyKey = new Map<string, string>();
  for (const ws of worksheets) {
    titleByPolicyKey.set(ws.policyKey, ws.title);
    for (const item of ws.items) labelByMapKey.set(`${ws.policyKey}.${item.itemKey}`, item.label);
  }

  const groups = new Map<string, NewFieldGroup>();
  for (const mapKey of POLICY_NEW_FIELD_MAPKEYS) {
    const policyKey = mapKey.split(".")[0];
    const group = groups.get(policyKey) ?? { policyKey, title: titleByPolicyKey.get(policyKey) ?? policyKey, fields: [] };
    group.fields.push({ mapKey, label: labelByMapKey.get(mapKey) ?? mapKey });
    groups.set(policyKey, group);
  }
  const orderedGroups = [...groups.values()].sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div className="mx-auto max-w-5xl p-6">
      <section>
        <h2 className="heading-base mb-1">New information to fill out</h2>
        <p className="body-sm mb-4 text-grey-500">
          These Cortex V3 policy fields have no legacy source — they're new and must be filled out directly in Policies. Applying the Policies step
          opens one Swivl-Support ticket listing them as tasks to help onboard the operator.
        </p>
        <div className="flex flex-col gap-6">
          {orderedGroups.map((group) => (
            <div key={group.policyKey}>
              <h3 className="body-sm-alt mb-2 text-grey-700">{group.title}</h3>
              <ul className="divide-y divide-grey-100 rounded border border-grey-200 bg-white">
                {group.fields.map((f) => (
                  <li key={f.mapKey} className="body-sm px-3 py-2 text-grey-700">
                    {f.label}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
